import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type {
  ChangeSetInfo,
  CredentialMetadata,
  DoDItem,
  Effort,
  EncryptedEnvelope,
  ExecutionEnvironment,
  GitStatusCheck,
  ModelDefault,
  ModelDefaultRole,
  MonthlyUsageStatus,
  ProviderType,
  RecentChat,
  UsageLimit,
  UsageRange,
  UsageReport,
} from "@racler/protocol";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

export interface User {
  id: string;
  displayName: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const isMutating = method === "POST" || method === "PUT" || method === "DELETE";
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: isMutating ? { "content-type": "application/json" } : {},
    body: isMutating ? JSON.stringify(body ?? {}) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? "ERROR", data.message ?? `HTTP ${res.status}`);
  return data as T;
}

export const api = {
  session: () => request<{ authenticated: boolean; user?: User }>("GET", "/api/auth/session"),

  async login(): Promise<User> {
    const { ceremonyId, options } = await request<{
      ceremonyId: string;
      options: PublicKeyCredentialRequestOptionsJSON;
    }>("POST", "/api/auth/login/options", {});
    const credential = await startAuthentication({ optionsJSON: options });
    return (await request<{ user: User }>("POST", "/api/auth/login/verify", { ceremonyId, credential })).user;
  },

  checkInvite: (inviteToken: string) =>
    request<{ valid: boolean }>("POST", "/api/auth/invite/check", { inviteToken }),

  async register(inviteToken: string, displayName: string): Promise<User> {
    const { ceremonyId, options } = await request<{
      ceremonyId: string;
      options: PublicKeyCredentialCreationOptionsJSON;
    }>("POST", "/api/auth/register/options", { inviteToken, displayName });
    const credential = await startRegistration({ optionsJSON: options });
    return (await request<{ user: User }>("POST", "/api/auth/register/verify", { ceremonyId, credential }))
      .user;
  },

  async addPasskey(): Promise<void> {
    const { ceremonyId, options } = await request<{
      ceremonyId: string;
      options: PublicKeyCredentialCreationOptionsJSON;
    }>("POST", "/api/auth/passkeys/options", {});
    const credential = await startRegistration({ optionsJSON: options });
    await request("POST", "/api/auth/passkeys/verify", { ceremonyId, credential });
  },

  /** Owner revocation (session + CSRF + ownership on the server). Idempotent: an already revoked Machine also succeeds. */
  revokeMachine: (machineId: string) =>
    request<{ ok: boolean; machineId: string; alreadyRevoked: boolean }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/revoke`,
      {},
    ),

  recentChats: (params?: number | { limit?: number; search?: string }) => {
    let query = "";
    if (typeof params === "number") {
      query = `?limit=${params}`;
    } else if (params) {
      const q = new URLSearchParams();
      if (params.limit !== undefined) q.set("limit", String(params.limit));
      if (params.search) q.set("search", params.search);
      const str = q.toString();
      if (str) query = `?${str}`;
    }
    return request<{ chats: RecentChat[] }>("GET", `/api/chats/recent${query}`);
  },
  usage: (range: UsageRange) => request<UsageReport>("GET", `/api/usage?range=${range}`),

  logout: () => request("POST", "/api/auth/logout", {}),

  lookupPairing: (code: string) =>
    request<{ name: string; platform: string; fingerprint: string; expiresAt: string }>(
      "POST",
      "/api/pairing/lookup",
      {
        code,
      },
    ),
  approvePairing: (code: string, name?: string) =>
    request<{ machine: { id: string; name: string } }>("POST", "/api/pairing/approve", { code, name }),

  credentials: () => request<{ credentials: CredentialMetadata[] }>("GET", "/api/credentials"),

  getProvisioningKey: (machineId: string) =>
    request<{ machineId: string; publicKeySpki: string; online: boolean }>(
      "GET",
      `/api/machines/${encodeURIComponent(machineId)}/provisioning-key`,
    ),

  provisionCredential: (
    machineId: string,
    payload: {
      envelope: EncryptedEnvelope;
      credentialId: string;
      provider: ProviderType;
      label: string;
      baseUrl?: string;
    },
  ) =>
    request<{ ok: boolean; credentialId: string }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/credentials`,
      payload,
    ),

  testCredential: (machineId: string, credentialId: string) =>
    request<{ ok: boolean; modelCount?: number; error?: string }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/credentials/${encodeURIComponent(credentialId)}/test`,
      {},
    ),

  deleteCredential: (credentialId: string, machineId?: string) =>
    request<{ ok: boolean }>(
      "DELETE",
      `/api/credentials/${encodeURIComponent(credentialId)}${machineId ? `?machineId=${encodeURIComponent(machineId)}` : ""}`,
    ),

  modelDefaults: (projectId?: string) =>
    request<{ defaults: ModelDefault[] }>(
      "GET",
      `/api/model-defaults${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`,
    ),

  saveModelDefault: (d: {
    projectId?: string;
    role: ModelDefaultRole;
    provider: string;
    credentialId?: string;
    model: string;
    effort?: Effort;
  }) => request<{ ok: boolean }>("PUT", "/api/model-defaults", d),

  usageLimits: (projectId?: string, credentialId?: string) =>
    request<{
      limits: UsageLimit[];
      status: {
        account: MonthlyUsageStatus;
        project?: MonthlyUsageStatus;
        credential?: MonthlyUsageStatus;
      };
    }>(
      "GET",
      `/api/usage-limits${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}${
        credentialId ? `&credentialId=${encodeURIComponent(credentialId)}` : ""
      }`,
    ),

  saveUsageLimit: (l: UsageLimit) => request<{ ok: boolean }>("PUT", "/api/usage-limits", l),

  gitStatus: (machineId: string, projectId: string) =>
    request<GitStatusCheck>(
      "GET",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/git/status`,
    ),

  getDoD: (machineId: string, projectId: string) =>
    request<{ items: DoDItem[] }>(
      "GET",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/dod`,
    ),

  createEnvironment: (machineId: string, projectId: string, type: "worktree") =>
    request<{ environment: ExecutionEnvironment }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/environments`,
      { type },
    ),

  activeEnvironment: (machineId: string, projectId: string) =>
    request<{ environment: ExecutionEnvironment | null }>(
      "GET",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/environments/active`,
    ),

  discardEnvironment: (machineId: string, projectId: string, environmentId: string) =>
    request<{ ok: boolean }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(environmentId)}/discard`,
    ),

  finalizeEnvironment: (machineId: string, projectId: string, environmentId: string) =>
    request<{ changeSet: ChangeSetInfo; unsupportedPaths: { path: string; reason: string }[] }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(environmentId)}/finalize`,
    ),

  applyEnvironment: (machineId: string, projectId: string, environmentId: string, contentHash?: string) =>
    request<{ ok: boolean; changeSet: ChangeSetInfo }>(
      "POST",
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(environmentId)}/apply`,
      { contentHash },
    ),
};

export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof DOMException && err.name === "NotAllowedError")
    return "Passkey prompt was cancelled or timed out.";
  if (err instanceof Error) return err.message;
  return String(err);
}
